import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '@agms/ui'
import api, { assignableUsersApi, helpApi, leadApi, ticketApi, ticketTypeApi } from '../../lib/api'
import { KnownIssueBanner } from '../help/KnownIssueBanner'
import { TicketArticleSuggestions, TICKET_SUGGESTION_RECORD_WAIT_MS, waitForSuggestionRecord, type TicketSuggestionRecord } from './TicketArticleSuggestions'
import type { Lead, PaginatedResponse, Ticket, TicketCategory, TicketPriority, TicketSlaType, TicketSourceChannel, TicketType } from '../../lib/api'
import { usePermissions } from '../../hooks/useAuth'
import { apiErrorMessage } from '../../hooks/useErrorHandler'

interface MerchantOption {
  id: string
  dba_name: string
  fiserv_mid: string | null
}

interface CreateTicketModalProps {
  context?: {
    merchantId?: string
    merchantName?: string
    leadId?: string
    leadName?: string
  }
  onClose: () => void
  onCreated?: (ticket: Ticket) => void
}

type ContextMode = 'merchant' | 'lead'
type AssigneeMode = 'me' | 'teammate' | 'unassigned'

export function CreateTicketModal({ context, onClose, onCreated }: CreateTicketModalProps) {
  const queryClient = useQueryClient()
  const { canAssignTickets, canViewTickets } = usePermissions()
  const canAssign = canAssignTickets()
  const canLinkKnownIssues = canViewTickets()
  const [knownIssueIds, setKnownIssueIds] = useState<string[]>([])
  const knownIssueIdsRef = useRef<string[]>([])
  knownIssueIdsRef.current = knownIssueIds
  const dialogRef = useRef<HTMLDivElement>(null)
  const lockedMode: ContextMode | null = context?.merchantId ? 'merchant' : context?.leadId ? 'lead' : null
  const [mode, setMode] = useState<ContextMode>(lockedMode ?? 'merchant')
  const [search, setSearch] = useState('')
  const [merchantId, setMerchantId] = useState(context?.merchantId ?? '')
  const [leadId, setLeadId] = useState(context?.leadId ?? '')
  const [ticketTypeId, setTicketTypeId] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [priority, setPriority] = useState<TicketPriority>('normal')
  const [slaType, setSlaType] = useState<TicketSlaType>('1_day')
  const [slaCustomDays, setSlaCustomDays] = useState(14)
  const [category, setCategory] = useState<TicketCategory>('general')
  const [sourceChannel, setSourceChannel] = useState<TicketSourceChannel>('internal')
  const [assigneeMode, setAssigneeMode] = useState<AssigneeMode>('me')
  const [assignedTo, setAssignedTo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const suggestionRecordRef = useRef<TicketSuggestionRecord | null>(null)
  const suggestionPendingRef = useRef<Promise<TicketSuggestionRecord | null> | null>(null)
  const suggestionOpenedRef = useRef(false)
  const suggestionsShownRef = useRef(false)

  const { data: users } = useQuery({
    queryKey: ['assignable-users'],
    queryFn: assignableUsersApi.list,
    enabled: canAssign,
  })

  const { data: ticketTypes } = useQuery({
    queryKey: ['ticket-types'],
    queryFn: () => ticketTypeApi.list(),
  })

  const { data: merchantOptions } = useQuery({
    queryKey: ['ticket-merchant-picker', search],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<MerchantOption>>('/merchants', {
        params: { search: search.trim() || undefined, per_page: 25 },
      })
      return response.data.data
    },
    enabled: !lockedMode && mode === 'merchant',
  })

  const { data: leadOptions } = useQuery({
    queryKey: ['ticket-lead-picker', search],
    queryFn: async () => {
      const response = await leadApi.list({ search: search.trim() || undefined, per_page: 25 })
      return response.data
    },
    enabled: !lockedMode && mode === 'lead',
  })

  const createMutation = useMutation({
    mutationFn: () => {
      const payload = {
        merchant_id: mode === 'merchant' ? merchantId : undefined,
        lead_id: mode === 'lead' ? leadId : undefined,
        ticket_type_id: ticketTypeId || null,
        subject: subject.trim(),
        body: body.trim() || null,
        priority,
        sla_type: slaType,
        sla_custom_days: slaType === 'custom' ? slaCustomDays : null,
        category,
        source_channel: sourceChannel,
      } as Parameters<typeof ticketApi.create>[0]

      if (canAssign && assigneeMode === 'teammate') {
        payload.assigned_to = assignedTo
      } else if (canAssign && assigneeMode === 'unassigned') {
        payload.assigned_to = null
      }

      return ticketApi.create(payload)
    },
    onSuccess: (ticket) => {
      const ticketId = ticket.id
      const articleIds = canLinkKnownIssues ? [...knownIssueIdsRef.current] : []
      void (async () => {
        try {
          if (suggestionOpenedRef.current) {
            const record = suggestionRecordRef.current
              ?? await waitForSuggestionRecord(suggestionPendingRef.current, TICKET_SUGGESTION_RECORD_WAIT_MS)
            if (!record) return
            await helpApi.updateTicketSuggestion(record.id, { ticket_id: ticketId, token: record.token })
            return
          }
          if (suggestionsShownRef.current) {
            await helpApi.createTicketSuggestion({ ticket_id: ticketId })
          }
        } catch {
          // The ticket is already created. Suggestion tracking stays quiet.
        }
      })()
      if (articleIds.length > 0) {
        void (async () => {
          for (const articleId of articleIds) {
            try {
              await ticketApi.linkArticle(ticketId, { article_id: articleId, solved: false })
            } catch {
              // The ticket is already created. The article can be linked from the ticket.
            }
          }
        })()
      }
      void queryClient.invalidateQueries({ queryKey: ['tickets'] })
      void queryClient.invalidateQueries({ queryKey: ['merchant-tickets'] })
      void queryClient.invalidateQueries({ queryKey: ['lead-tickets'] })
      onCreated?.(ticket)
      onClose()
    },
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not create ticket.'))
    },
  })

  const recordSelected = mode === 'merchant' ? Boolean(merchantId) : Boolean(leadId)
  const eomPreview = useMemo(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth() + 1, 1).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }, [])

  const selectedTicketType = useMemo(
    () => (ticketTypes ?? []).find((ticketType) => ticketType.id === ticketTypeId) ?? null,
    [ticketTypeId, ticketTypes],
  )

  const groupedTicketTypes = useMemo(() => {
    const groups = new Map<string, TicketType[]>()
    ;(ticketTypes ?? []).forEach((ticketType) => {
      const key = ticketType.type_group?.trim() || 'Ungrouped'
      groups.set(key, [...(groups.get(key) ?? []), ticketType])
    })
    return Array.from(groups.entries())
  }, [ticketTypes])

  const applyTicketType = (id: string) => {
    setTicketTypeId(id)
    const ticketType = (ticketTypes ?? []).find((item) => item.id === id)
    if (!ticketType) return
    setSubject(ticketType.default_subject ?? '')
    setBody(ticketType.default_description ?? '')
    setPriority(ticketType.default_priority)
    setCategory(ticketType.default_category)
    setSlaType(ticketType.default_sla_type)
    setSlaCustomDays(ticketType.default_sla_custom_days ?? 14)
  }

  const canSubmit = subject.trim().length > 0
    && recordSelected
    && (!canAssign || assigneeMode !== 'teammate' || assignedTo.length > 0)
    && (slaType !== 'custom' || (slaCustomDays >= 1 && slaCustomDays <= 45))
    && !createMutation.isPending

  const lockedRecordLabel = useMemo(() => {
    if (context?.merchantId) return context.merchantName ?? context.merchantId
    if (context?.leadId) return context.leadName ?? context.leadId
    return null
  }, [context])

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const firstControl = dialogRef.current?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])')
    firstControl?.focus()

    return () => {
      previousFocus?.focus()
    }
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
      <div
        ref={dialogRef}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-ticket-title"
      >
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 id="create-ticket-title" className="text-lg font-semibold text-gray-900">Create Ticket</h2>
        </div>

        <div className="space-y-4 px-5 py-4">
          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
              {error}
            </div>
          )}

          <label className="text-sm font-medium text-gray-700">
            Ticket Type
            <select
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={ticketTypeId}
              onChange={(event) => applyTicketType(event.target.value)}
            >
              <option value="">No template</option>
              {groupedTicketTypes.map(([group, items]) => (
                <optgroup key={group} label={group}>
                  {items.map((ticketType) => (
                    <option key={ticketType.id} value={ticketType.id}>
                      {ticketType.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>

          {selectedTicketType && (
            <div className="rounded-md border border-blue-100 bg-blue-50/60 p-3">
              <div className="text-sm font-semibold text-blue-950">{selectedTicketType.name}</div>
              {selectedTicketType.additional_information && (
                <p className="mt-1 whitespace-pre-wrap text-xs text-blue-800">{selectedTicketType.additional_information}</p>
              )}
              {(selectedTicketType.checklist_items ?? []).length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-blue-900">Checklist preview</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-blue-900">
                    {(selectedTicketType.checklist_items ?? []).map((item) => (
                      <li key={item.id}>
                        {item.text}
                        {(item.comment_required_to_complete || item.file_required_to_complete) && (
                          <span className="text-blue-700">
                            {' '}({[
                              item.comment_required_to_complete ? 'comment' : null,
                              item.file_required_to_complete ? 'file' : null,
                            ].filter(Boolean).join(' + ')} required)
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              maxLength={500}
              placeholder="Short summary"
            />

            <label className="text-sm font-medium text-gray-700">
              Source channel
              <select
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={sourceChannel}
                onChange={(event) => setSourceChannel(event.target.value as TicketSourceChannel)}
              >
                <option value="internal">Internal</option>
                <option value="phone">Phone</option>
                <option value="email">Email</option>
              </select>
            </label>
          </div>

          <label className="text-sm font-medium text-gray-700">
            Description
            <textarea
              className="mt-1 block min-h-[110px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="What happened? What does the team need to do?"
            />
          </label>

          <TicketArticleSuggestions
            subject={subject}
            body={body}
            onOutcomeChange={(record) => {
              suggestionRecordRef.current = record
            }}
            onPendingRecord={(pending) => {
              suggestionPendingRef.current = pending
            }}
            onOpened={() => {
              suggestionOpenedRef.current = true
            }}
            onShown={() => {
              suggestionsShownRef.current = true
            }}
          />

          <KnownIssueBanner
            linkable={canLinkKnownIssues}
            selectedIds={knownIssueIds}
            onToggle={(articleId, checked) => {
              setKnownIssueIds((current) => {
                if (checked) return current.includes(articleId) ? current : [...current, articleId]
                return current.filter((id) => id !== articleId)
              })
            }}
          />

          <div className="grid gap-4 md:grid-cols-3">
            <label className="text-sm font-medium text-gray-700">
              Priority
              <select
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={priority}
                onChange={(event) => setPriority(event.target.value as TicketPriority)}
              >
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>

            <label className="text-sm font-medium text-gray-700">
              Category
              <select
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={category}
                onChange={(event) => setCategory(event.target.value as TicketCategory)}
              >
                <option value="billing">Billing</option>
                <option value="technical">Technical</option>
                <option value="onboarding">Onboarding</option>
                <option value="general">General</option>
              </select>
            </label>

          </div>

          <div className="rounded-md border border-gray-200 p-3">
            <div className="text-sm font-medium text-gray-700">SLA</div>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {[
                ['1_day', 'Respond within 1 day'],
                ['2_3_days', 'Respond within 3 days'],
                ['7_days', 'Respond within 7 days'],
                ['beginning_of_next_month', `Beginning of next month (${eomPreview})`],
                ['custom', 'Custom days'],
              ].map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    checked={slaType === value}
                    onChange={() => setSlaType(value as TicketSlaType)}
                  />
                  {label}
                </label>
              ))}
            </div>
            {slaType === 'custom' && (
              <label className="mt-3 block text-sm font-medium text-gray-700">
                Custom days
                <input
                  type="number"
                  min={1}
                  max={45}
                  className="mt-1 block w-32 rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={slaCustomDays}
                  onChange={(event) => setSlaCustomDays(Number(event.target.value))}
                />
              </label>
            )}
          </div>

          {canAssign && (
          <div className="rounded-md border border-gray-200 p-3">
            <div className="text-sm font-medium text-gray-700">Assignee</div>
            <div className="mt-2 grid gap-2 md:grid-cols-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  checked={assigneeMode === 'me'}
                  onChange={() => setAssigneeMode('me')}
                />
                Assign to me
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  checked={assigneeMode === 'teammate'}
                  onChange={() => setAssigneeMode('teammate')}
                />
                Assign to teammate
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  checked={assigneeMode === 'unassigned'}
                  onChange={() => setAssigneeMode('unassigned')}
                />
                Leave unassigned
              </label>
            </div>
            {assigneeMode === 'teammate' && (
              <select
                className="mt-3 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={assignedTo}
                onChange={(event) => setAssignedTo(event.target.value)}
              >
                <option value="">Select teammate...</option>
                {(users ?? []).map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            )}
          </div>
          )}

          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            {lockedMode ? (
              <p className="text-sm text-gray-700">
                Linked to {lockedMode}: <span className="font-semibold">{lockedRecordLabel}</span>
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button type="button" variant={mode === 'merchant' ? 'primary' : 'secondary'} onClick={() => setMode('merchant')}>
                    Merchant
                  </Button>
                  <Button type="button" variant={mode === 'lead' ? 'primary' : 'secondary'} onClick={() => setMode('lead')}>
                    Lead
                  </Button>
                </div>
                <Input
                  label={`Search ${mode === 'merchant' ? 'merchants' : 'leads'}`}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by DBA, legal name, email, or MID"
                />
                {mode === 'merchant' ? (
                  <select
                    aria-label="Select merchant for ticket"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={merchantId}
                    onChange={(event) => setMerchantId(event.target.value)}
                  >
                    <option value="">Select merchant...</option>
                    {(merchantOptions ?? []).map((merchant) => (
                      <option key={merchant.id} value={merchant.id}>
                        {merchant.dba_name} {merchant.fiserv_mid ? `- MID ${merchant.fiserv_mid}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    aria-label="Select lead for ticket"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={leadId}
                    onChange={(event) => setLeadId(event.target.value)}
                  >
                    <option value="">Select lead...</option>
                    {(leadOptions ?? []).map((lead: Lead) => (
                      <option key={lead.id} value={lead.id}>
                        {lead.dba_name ?? lead.legal_name ?? lead.id}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => createMutation.mutate()} disabled={!canSubmit}>
            {createMutation.isPending ? 'Creating...' : 'Create Ticket'}
          </Button>
        </div>
      </div>
    </div>
  )
}
